import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { Microphone, PaperPlaneTilt, Sparkle, SpeakerHigh } from "@phosphor-icons/react";
import { toast } from "sonner";

const SESSION_ID = "primary";

export default function AIGuide() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [ttsOn, setTtsOn] = useState(false);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);

  // Load history
  useEffect(() => {
    api.get(`/ai/history/${SESSION_ID}`).then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  // Autoscroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // Web Speech API
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = "en-IN";
    r.onresult = (e) => {
      const t = Array.from(e.results).map((r) => r[0].transcript).join(" ");
      setInput(t);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recognitionRef.current = r;
  }, []);

  const speak = (text) => {
    if (!ttsOn || !window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-IN";
    u.rate = 0.98;
    window.speechSynthesis.speak(u);
  };

  const toggleMic = () => {
    if (!recognitionRef.current) { toast.error("Voice input not supported in this browser"); return; }
    if (listening) recognitionRef.current.stop();
    else {
      setInput("");
      recognitionRef.current.start();
      setListening(true);
    }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", text: q, id: Date.now() }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: SESSION_ID, text: q });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, id: Date.now() + 1 }]);
      speak(data.reply);
    } catch (e) {
      toast.error("Priya could not respond. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    "I've been having trouble sleeping",
    "What should I eat for better energy?",
    "I feel stressed at work",
    "Help me find a good general physician",
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-screen bg-bone">
      {/* Header */}
      <div className="px-6 md:px-12 py-6 border-b border-black/5 flex items-center justify-between">
        <div>
          <div className="overline text-terracotta">AI Health Guide</div>
          <h1 className="font-heritage text-3xl text-ayurveda mt-1">Priya</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={ttsOn ? "default" : "outline"}
            size="sm"
            className={ttsOn ? "bg-ayurveda text-bone" : ""}
            onClick={() => setTtsOn(!ttsOn)}
            data-testid="ai-tts-toggle"
          >
            <SpeakerHigh size={16} className="mr-1" /> {ttsOn ? "Voice on" : "Voice off"}
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-12 py-6 space-y-4">
        {messages.length === 0 && !busy && (
          <div className="flex flex-col items-center justify-center h-full text-center max-w-lg mx-auto">
            <motion.div
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="ai-orb w-40 h-40 rounded-full mb-8"
            />
            <div className="overline text-terracotta">I'm listening</div>
            <h2 className="font-heritage text-3xl text-ayurveda mt-2 mb-3">How can I help you today?</h2>
            <p className="text-sm text-charcoal/60 mb-6">I speak English, Malayalam, Hindi, Tamil and more. I'm not a doctor — but I'll gently guide you and connect you to one.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-left px-4 py-3 rounded-xl border border-black/5 bg-white hover:border-ayurveda hover:-translate-y-0.5 transition-all text-sm text-charcoal/80"
                  data-testid={`ai-suggest-${s.slice(0,10).replace(/\s/g,'-')}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m) => (
            <motion.div
              key={m.id || m.created_at}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] md:max-w-2xl rounded-2xl px-5 py-3 ${
                  m.role === "user"
                    ? "bg-ayurveda text-bone rounded-br-md"
                    : "bg-white border border-black/5 text-charcoal rounded-bl-md"
                }`}
              >
                {m.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-1 text-terracotta">
                    <Sparkle size={12} weight="fill" />
                    <span className="overline text-[10px]">Priya</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap leading-relaxed text-[15px]">{m.text}</div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {busy && (
          <div className="flex justify-start">
            <div className="bg-white border border-black/5 rounded-2xl px-5 py-3 flex items-center gap-2">
              <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity }} className="w-2 h-2 rounded-full bg-terracotta" />
              <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }} className="w-2 h-2 rounded-full bg-terracotta" />
              <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }} className="w-2 h-2 rounded-full bg-terracotta" />
              <span className="text-xs text-charcoal/50 ml-2">Priya is thinking…</span>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-black/5 bg-white p-4 md:p-6">
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="max-w-3xl mx-auto flex items-center gap-2"
          data-testid="ai-composer"
        >
          <Button
            type="button"
            variant={listening ? "default" : "outline"}
            size="icon"
            onClick={toggleMic}
            className={`shrink-0 h-11 w-11 ${listening ? "bg-terracotta text-white animate-breathe" : ""}`}
            data-testid="ai-mic-btn"
            aria-label="Toggle voice input"
          >
            <Microphone size={20} weight={listening ? "fill" : "duotone"} />
          </Button>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={listening ? "Listening…" : "Ask Priya anything about your health…"}
            className="h-11 bg-bone border-black/10"
            data-testid="ai-input"
          />
          <Button type="submit" disabled={busy || !input.trim()} className="shrink-0 h-11 bg-ayurveda hover:bg-ayurveda/90 text-bone" data-testid="ai-send-btn">
            <PaperPlaneTilt size={18} />
          </Button>
        </form>
        <div className="max-w-3xl mx-auto mt-2 text-[11px] text-charcoal/50 text-center">
          Priya is an AI guide from Gutvibe Health — not a diagnosis. For emergencies dial 112 or 108.
        </div>
      </div>
    </div>
  );
}
